import { Destination, FreezeInput, Id, parse } from '../../../../src/apps/selection/contracts.mjs';
import {
  InspectActionTicket,
  InspectFrozenResult,
  parseInspectReceipt,
} from '../../../../src/apps/selection/host-contracts.mjs';
import {
  projectInspectTarget,
  bindInspectChanges,
  changesForInspectProperty,
} from './inspect-projection.mjs';

const refused = (error) => ({
  status: 'refused',
  reasonKey: error?.code ?? error?.message ?? 'selection_unavailable',
});
const stale = (error) =>
  ['stale_session', 'stale_revision', 'document_replaced'].includes(error?.code);

function receiptProjection(value, expected) {
  const receipt = parseInspectReceipt(value, expected);
  return receipt.status === 'committed' ? Object.freeze(receipt.value) : null;
}

// One facade over INS's one controller. Original operations and Undo references
// remain separate from a Send callback's captured choice and frozen promise.
export function createInspectSessionProjection({
  domain,
  view,
  labels,
  describeTarget,
  canEdit,
  subjectLabel = null,
  issueHostIntent,
  readCommitReceipt,
  originalUndo,
  describePrepared,
  createOperationId,
}) {
  for (const port of [
    describeTarget,
    canEdit,
    issueHostIntent,
    readCommitReceipt,
    describePrepared,
    createOperationId,
  ])
    if (typeof port !== 'function')
      throw new TypeError('inspect_projection_dependency_unavailable');
  let previous = null;
  let projected = null;
  let dispatched = null;
  let committed = null;
  let committedTicket = null;
  let restored = false;
  let effectDispatched = false;
  let dispatching = false;

  function getSnapshot() {
    const snapshot = domain.getSnapshot();
    if (snapshot === previous) return projected;
    previous = snapshot;
    projected = Object.freeze({
      state: snapshot.state,
      sessionId: snapshot.binding.sessionId,
      revision: snapshot.binding.revisionId,
      canEdit: canEdit(snapshot) === true,
      subjectLabel,
      targets: snapshot.targets.map((target, index) =>
        projectInspectTarget(target, index + 1, { view, labels, describeTarget }),
      ),
      limit: snapshot.limit,
    });
    return projected;
  }

  function captureSelection() {
    const snapshot = domain.getSnapshot();
    if (!snapshot.targets.length) return null;
    if (snapshot.state !== 'on' || snapshot.targets.some((target) => target.status !== 'current'))
      throw new TypeError('stale_session');
    let originalDestination = null;
    let frozen = null;
    return (rawDestination) => {
      const destination = parse(Destination, rawDestination);
      if (originalDestination) {
        if (
          destination.kind !== originalDestination.kind ||
          destination.id !== originalDestination.id
        )
          throw new TypeError('selection_destination_mismatch');
        return frozen;
      }
      if (domain.getSnapshot() !== snapshot) throw new TypeError('stale_session');
      originalDestination = destination;
      // Capture before the submitted-thread mirror; dispatch only after that mirror.
      // A continuation uses this original promise, never a later live selection.
      frozen = actions.freeze(destination);
      return frozen;
    };
  }

  async function prepareChanges(input) {
    try {
      if (dispatched) throw new TypeError('receipt_reconciliation_required');
      const snapshot = domain.getSnapshot();
      if (!canEdit(snapshot)) throw new TypeError('not_authorized');
      if (snapshot.targets.some((target) => view?.targetIdFor(target.pid) !== target.targetId))
        throw new TypeError('selection_unavailable');
      const changes = bindInspectChanges(snapshot, input);
      const prepared = await domain.prepare(changes);
      const display = describePrepared(prepared, changes);
      if (typeof display.before !== 'string' || typeof display.after !== 'string')
        throw new TypeError('prepared_display_unavailable');
      Id.parse(prepared.operationId);
      return {
        status: 'ready',
        operationId: prepared.operationId,
        before: display.before,
        after: display.after,
        scopeLabel: display.scopeLabel ?? null,
      };
    } catch (error) {
      return refused(error);
    }
  }

  async function reconcile(ticket) {
    const result = receiptProjection(await readCommitReceipt(ticket), ticket);
    if (!result) return { status: 'failed', reasonKey: 'receipt_reconciliation_required' };
    // The old action is independent of a genuinely admitted replacement view.
    if (effectDispatched && domain.getSnapshot().state !== 'unavailable')
      domain.invalidate('revision_changed');
    if (ticket.action === 'apply') committed = result;
    else committed = null;
    committedTicket = ticket.action === 'apply' ? ticket : null;
    if (restored) dispatched = null;
    return {
      status: 'saved',
      ...(result.versionLabel === null ? {} : { versionLabel: result.versionLabel }),
    };
  }

  async function restoreOriginal(raw) {
    if (dispatching) return { status: 'failed', reasonKey: 'operation_in_progress' };
    dispatching = true;
    try {
      const ticket = Object.freeze(InspectActionTicket.parse(raw));
      if (ticket.artifactId !== domain.getSnapshot().binding.artifactId)
        throw new TypeError('receipt_identity_mismatch');
      if (
        dispatched &&
        (Object.keys(dispatched).length !== Object.keys(ticket).length ||
          Object.keys(ticket).some((key) => dispatched[key] !== ticket[key]))
      )
        return { status: 'failed', reasonKey: 'receipt_reconciliation_required' };
      // Ticket is an original journal lookup, never a receipt, edit or preview grant.
      // Retain an unknown original so no replacement action can be dispatched.
      restored = true;
      if (!dispatched) effectDispatched = false;
      dispatched = ticket;
      committed = null;
      committedTicket = null;
      return await reconcile(ticket);
    } catch {
      return {
        status: 'failed',
        reasonKey: dispatched ? 'receipt_reconciliation_required' : 'original_action_invalid',
      };
    } finally {
      dispatching = false;
    }
  }

  async function apply(operationId) {
    if (dispatching) return { status: 'failed', reasonKey: 'operation_in_progress' };
    dispatching = true;
    try {
      // Lost/late response is read-only reconciliation, never another apply.
      if (dispatched) {
        if (dispatched.action !== 'apply' || dispatched.operationId !== operationId)
          return { status: 'failed', reasonKey: 'receipt_reconciliation_required' };
        return await reconcile(dispatched);
      }
      const snapshot = domain.getSnapshot();
      const prepared = snapshot.prepared;
      if (!canEdit(snapshot) || snapshot.state !== 'on' || prepared?.operationId !== operationId)
        return { status: 'stale', reasonKey: 'prepared_operation_required' };
      const request = {
        action: 'apply',
        ...snapshot.binding,
        operationId,
        operationDigest: prepared.operationDigest,
      };
      const hostIntent = Id.parse(await issueHostIntent(request));
      if (domain.getSnapshot() !== snapshot) return { status: 'stale', reasonKey: 'stale_session' };
      dispatched = Object.freeze(request);
      restored = false;
      effectDispatched = true;
      committed = null;
      committedTicket = null;
      // INS requires this server-issued intent after the host Apply gesture.
      await domain.apply(operationId, hostIntent);
      return await reconcile(dispatched);
    } catch (error) {
      return {
        // Once the original mutation was dispatched, even an authority error
        // may follow a commit. Only its original receipt can settle the result.
        status: !dispatched && stale(error) ? 'stale' : 'failed',
        reasonKey: dispatched
          ? 'receipt_reconciliation_required'
          : (error?.code ?? 'host_intent_unavailable'),
      };
    } finally {
      dispatching = false;
    }
  }

  async function undo() {
    if (dispatching) return { status: 'failed', reasonKey: 'operation_in_progress' };
    dispatching = true;
    try {
      if (dispatched?.action === 'undo') return await reconcile(dispatched);
      if (!committed) return { status: 'failed', reasonKey: 'undo_unavailable' };
      if (restored && typeof originalUndo !== 'function')
        return { status: 'failed', reasonKey: 'original_undo_unavailable' };
      const snapshot = domain.getSnapshot();
      if (snapshot.binding.artifactId !== committed.artifactId) return { status: 'conflict' };
      const request = {
        action: 'undo',
        sessionId: committedTicket.sessionId,
        artifactId: committedTicket.artifactId,
        revisionId: committedTicket.revisionId,
        buildId: committedTicket.buildId,
        operationId: committed.operationId,
        expectedRevisionId: committed.revisionId,
        idempotencyKey: Id.parse(createOperationId()),
      };
      const hostIntent = Id.parse(await issueHostIntent(request));
      if (domain.getSnapshot() !== snapshot) return { status: 'conflict' };
      dispatched = Object.freeze(request);
      effectDispatched = true;
      const input = {
        operationId: request.operationId,
        expectedRevisionId: request.expectedRevisionId,
        idempotencyKey: request.idempotencyKey,
        hostIntent,
      };
      if (restored) await originalUndo(dispatched, input);
      else await domain.undo(input);
      return await reconcile(dispatched);
    } catch (error) {
      return {
        status: !dispatched && error?.code === 'undo_conflict' ? 'conflict' : 'failed',
        reasonKey:
          dispatched?.action === 'undo'
            ? 'receipt_reconciliation_required'
            : (error?.code ?? 'host_intent_unavailable'),
      };
    } finally {
      dispatching = false;
    }
  }

  const actions = Object.freeze({
    setOn: (on) => domain.setOn(on),
    beginRequest: (id, kind) => domain.beginRequest(id, kind),
    invalidate: (code) => domain.invalidate(code),
    async resolve(candidate, identity) {
      const target = await domain.resolve(candidate, identity);
      if (!target || view?.targetIdFor(target.pid) !== target.targetId) return null;
      return { targetId: target.targetId, label: view.label(target.pid) };
    },
    add: (id, options) => domain.add(id, options),
    remove: (id) => domain.remove(id),
    clear: () => domain.clear(),
    freeze: async (destination, cropRefs = []) =>
      parse(
        InspectFrozenResult,
        await domain.freeze(
          parse(Destination, destination),
          parse(FreezeInput.shape.cropRefs, cropRefs),
        ),
      ),
    async prepare(property, value) {
      try {
        return await prepareChanges(
          changesForInspectProperty(domain.getSnapshot(), property, value),
        );
      } catch (error) {
        return refused(error);
      }
    },
    prepareChanges,
    apply,
    cancel: () => domain.cancel(),
    undo,
  });
  return Object.freeze({
    captureSelection,
    restoreOriginal,
    getSnapshot,
    subscribe: (listener) => domain.subscribe(listener),
    project: () => Object.freeze({ ...getSnapshot(), ...actions }),
    actions,
  });
}
